import {SortingState} from '@tanstack/table-core';
import {MRT_ColumnDef} from 'material-react-table';
import React, {useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {Box, CircularProgress, Alert} from '@mui/material';
import {Headline} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {type BrandedContentAdsPartnership} from '@/types/brandedContentAdsPartnership';
import BrandedContentEmptyResult from '@/components/branded-content/empty-result';
import Table from '@/components/table';
import useAgencyQuery from '@/hooks/queries/use-agency-query';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useBrandedContentAdsPartnershipsQuery from '@/hooks/queries/useBrandedContentAdsPartnershipsQuery';
import useBrandId from '@/hooks/use-brand-id';
import useNavigation from '@/hooks/use-navigation';
import styles from './Partnerships.module.scss';
import InstagramAccountConfigCard from './components/instagram-account-config-card';
import tableStyles from './partnerships-table/PartnershipsTable.module.scss';
import {
  CreatorCell,
  StatusCell,
  CreatedCell,
  UpdatedCell,
} from './partnerships-table/cells';

interface PartnershipsProps {
  testID?: string;
}

function getPartnershipTableColumns(): MRT_ColumnDef<BrandedContentAdsPartnership>[] {
  const columnAlignment = (align: 'left' | 'center' | 'right') => ({
    muiTableHeadCellProps: {align},
    muiTableBodyCellProps: {align},
  });

  const columnSizes = {size: 100, grow: 1};

  return [
    {
      header: translate('creator-name'),
      Header: translate('creator').toUpperCase(),
      Cell: CreatorCell,
      accessorKey: 'profile.displayName',
      enableSorting: true,
      ...columnSizes,
      size: 200,
      grow: 0,
      ...columnAlignment('left'),
    },
    {
      header: translate('status'),
      Header: translate('status').toUpperCase(),
      Cell: StatusCell,
      accessorKey: 'status',
      enableSorting: true,
      ...columnSizes,
      ...columnAlignment('center'),
    },
    {
      header: translate('created'),
      Header: translate('created').toUpperCase(),
      Cell: CreatedCell,
      accessorKey: 'createdAt',
      enableSorting: true,
      ...columnSizes,
      ...columnAlignment('center'),
    },
    {
      header: translate('updated'),
      Header: translate('updated').toUpperCase(),
      Cell: UpdatedCell,
      accessorKey: 'updatedAt',
      enableSorting: true,
      ...columnSizes,
      ...columnAlignment('center'),
    },
  ];
}

/**
 * This route renders a list of creators that the brand has requested Partnership Ads permissions from using Meta's APIs
 */
function Partnerships({testID = 'partnerships'}: PartnershipsProps) {
  const brandId = useBrandId();
  const {dashboardUser} = useDashboardUserQuery({});
  const {navigate} = useNavigation();
  const [searchParams] = useSearchParams();
  const {currentBrand} = useAgencyQuery({brandId: brandId || ''});

  const {
    data: partnerships = [],
    isLoading,
    error,
  } = useBrandedContentAdsPartnershipsQuery({
    brandId: brandId || '',
    enabled: !!brandId,
  });

  const hasPartnerships = partnerships.length > 0;
  const tableRef = useRef<HTMLTableElement>(null);
  const [sorting, setSorting] = useState<SortingState>([]);

  const tableColumns = getPartnershipTableColumns();

  const isRowDisabled = (row: BrandedContentAdsPartnership) => {
    // Only allow clicking on rows with 'Approved' status
    return row.status !== 'Approved';
  };

  const onRowClick = (row: BrandedContentAdsPartnership) => {
    // Only navigate if the row is not disabled (i.e., status is 'Approved')
    if (row.status === 'Approved') {
      const newSearchParams = new URLSearchParams(searchParams);
      if (brandId) {
        newSearchParams.set('brandId', brandId);
      }
      navigate(`/partnerships/create/${row.id}?${newSearchParams.toString()}`, {
        state: {partnership: row, brandId},
      });
    }
  };

  // Show loading if we don't have essential data yet
  if (!dashboardUser || isLoading) {
    return (
      <Box
        className={styles.loadingContainer}
        data-testid={`${testID}-loading`}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles.errorContainer} data-testid={`${testID}-error`}>
        <Alert severity="error">
          {translate('Failed to load partnerships. Please try again.')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box className={styles.container} data-testid={testID}>
      {!hasPartnerships && <BrandedContentEmptyResult />}
      <InstagramAccountConfigCard
        testID={`${testID}--config-card`}
        brandInstagramBusinessAccount={
          currentBrand?.brandInstagramBusinessAccount
        }
      />
      {hasPartnerships && (
        <Headline size="md" className={styles.heading}>
          {translate('Partnership Creators List')}
        </Headline>
      )}
      {hasPartnerships && (
        <div className={tableStyles.container}>
          <Table
            tableRef={tableRef}
            testID={`${testID}-table`}
            classes={{
              container: undefined,
            }}
            columns={tableColumns}
            data={partnerships}
            isLoadingInitial={isLoading}
            showProgressBars={false}
            visibleColumns={{}}
            rowSelection={{}}
            isRowDisabled={isRowDisabled}
            sorting={sorting}
            onSorting={setSorting}
            isLoading={isLoading}
            enableVirtualization
            enableSorting
            manualSorting={false}
            onRowClick={onRowClick}
          />
        </div>
      )}
    </Box>
  );
}

export default Partnerships;
